// Connectivity observation only: never replay a submission or alter its result.
export const SERVICE_INTERRUPTION = 'INSIGHT is temporarily unavailable. Please try again shortly.';
export const CONNECTION_INTERRUPTION = 'Connection interrupted. Checking the connection.';
const gatewayStatuses = new Set([502, 503, 504, 522, 523, 524]);
function isApiRequest(config, api) {
  try {
    const base = new URL(api, window.location.origin);
    const target = new URL(config?.url || '', config?.baseURL || window.location.origin);
    if (target.origin !== base.origin || !target.pathname.startsWith(`${base.pathname}/`)) return false;
  } catch (_) { return false; }
  return true;
}
export function needsConnectivityCheck(error, api) {
  if (!error || error.code === 'ERR_CANCELED' || error.__CANCEL__ || !isApiRequest(error.config, api)) return false;
  if (error.response) return gatewayStatuses.has(error.response.status);
  return ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error.code);
}

export function createAvailabilityMonitor({ probe, notify, dismiss, now = Date.now, schedule = setTimeout, cancel = clearTimeout }) {
  let pending = false, lastCheck = -Infinity, timer, visible = false, disposed = false, recoveries = 0, epoch = 0;
  const clear = () => {
    epoch += 1;
    if (timer) cancel(timer);
    timer = undefined;
    if (visible) dismiss();
    visible = false;
    recoveries = 0;
  };
  const check = async () => {
    if (disposed || pending || now() - lastCheck < 15000) return;
    pending = true;
    lastCheck = now();
    const started = epoch;
    const results = [];
    try {
      for (let i = 0; i < 2; i += 1) {
        try {
          const response = await probe();
          if (response.status === 200 && response.data?.status === 'ok') { clear(); return; }
          // An unexpected health response is not proof of an outage.
          return;
        } catch (error) {
          if (disposed || epoch !== started) return;
          if (error.response && !gatewayStatuses.has(error.response.status)) return;
          results.push(error.response?.status);
        }
      }
      if (disposed || epoch !== started) return;
      visible = true;
      notify(results.every(s => gatewayStatuses.has(s)) ? SERVICE_INTERRUPTION : CONNECTION_INTERRUPTION, check);
      // Bounded recovery checks; an online event or a subsequent failed API call
      // can check again after this budget. No background polling while healthy.
      if (recoveries++ < 4) timer = schedule(check, 30000);
    } finally { pending = false; }
  };
  return { check, recovered: clear, dispose: () => { disposed = true; clear(); } };
}

export function installBackendAvailability(axios, api, toast) {
  // A separate client avoids auth admission, loading overlays and recursive errors.
  const health = axios.create();
  const monitor = createAvailabilityMonitor({
    probe: () => health.get(`${api}/health`, { timeout: 8000, headers: { 'Cache-Control': 'no-cache' } }),
    notify: (message, check) => toast.error(message, { id: 'backend-connection', duration: Infinity, action: { label: 'Check connection', onClick: check } }),
    dismiss: () => toast.dismiss('backend-connection'),
  });
  const id = axios.interceptors.response.use(response => {
    if (response.status >= 200 && response.status < 300 && isApiRequest(response.config, api)) monitor.recovered();
    return response;
  }, error => {
    if (needsConnectivityCheck(error, api)) void monitor.check();
    return Promise.reject(error);
  });
  const online = () => { void monitor.check(); };
  window.addEventListener('online', online);
  return () => { axios.interceptors.response.eject(id); window.removeEventListener('online', online); monitor.dispose(); };
}
